import React, { useEffect, useState } from 'react';
import { usePlayer } from '../lib/PlayerContext';
import { PlayerControls } from './PlayerControls';
import AuthModal from './auth/AuthModal';
// import { useAuth } from '../context/AuthContext'; // Currently unused
import { PerformanceDashboard } from './PerformanceDashboard';
import { Sidebar } from './layout/Sidebar';
import { Header } from './layout/Header';
import { FullScreenPlayer } from './player/FullScreenPlayer';
import { MiniPlayer } from './player/MiniPlayer';
import { useScreenReader } from '../context/AccessibilityContext';
import { useLocation } from 'react-router-dom';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import PictureInPictureIcon from '@mui/icons-material/PictureInPicture';

export function Layout({ children }: { children: React.ReactNode }) {
  const player = usePlayer();
  // const { user } = useAuth(); // Currently unused but may be needed for auth-related features
  const location = useLocation();
  const { announcePageChange } = useScreenReader();
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [fullScreenPlayerOpen, setFullScreenPlayerOpen] = useState(false);
  const [miniPlayerOpen, setMiniPlayerOpen] = useState(false);
  const [playerMode, setPlayerMode] = useState<'normal' | 'mini' | 'fullscreen'>('normal');
  
  // Announce page changes for screen readers
  useEffect(() => {
    const pageName = getPageName(location.pathname);
    if (pageName) {
      announcePageChange(pageName);
    }
  }, [location.pathname, announcePageChange]);

  // Helper function to get readable page names
  const getPageName = (pathname: string): string => {
    const routes: Record<string, string> = {
      '/': 'Home',
      '/search': 'Search',
      '/library': 'Library',
      '/analytics': 'Analytics',
      '/profile': 'Profile',
      '/albums': 'Albums',
      '/ui-showcase': 'UI Showcase',
      '/about': 'About',
      '/help': 'Help',
      '/forartists': 'For Artists',
      '/changelog': 'Changelog',
    };

    // Handle dynamic routes
    if (pathname.startsWith('/artist/')) return 'Artist Page';
    if (pathname.startsWith('/album/')) return 'Album Page';
    if (pathname.startsWith('/song/')) return 'Song Page';
    if (pathname.startsWith('/voicebank/')) return 'Voicebank Page';
    if (pathname.startsWith('/user/')) return 'User Profile';

    return routes[pathname] || 'Page';
  };

  // Close mobile sidebar when switching to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setMobileSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleToggleSidebar = () => {
    if (window.innerWidth < 768) {
      setMobileSidebarOpen(!mobileSidebarOpen);
    } else {
      setSidebarCollapsed(!sidebarCollapsed);
    }
  };

  // Player mode handlers
  const handleOpenMiniPlayer = () => {
    setPlayerMode('mini');
    setMiniPlayerOpen(true);
    setFullScreenPlayerOpen(false);
  };

  const handleOpenFullScreenPlayer = () => {
    setPlayerMode('fullscreen');
    setFullScreenPlayerOpen(true);
    setMiniPlayerOpen(false);
  };

  const handleCloseAllPlayers = () => {
    setPlayerMode('normal');
    setMiniPlayerOpen(false);
    setFullScreenPlayerOpen(false);
  };

  const handleMiniPlayerMaximize = () => {
    setPlayerMode('fullscreen');
    setMiniPlayerOpen(false);
    setFullScreenPlayerOpen(true);
  };

  return (
    <div className="flex h-screen bg-deep-night overflow-hidden">
      {/* Authentication Modal */}
      <AuthModal 
        isOpen={isAuthModalOpen} 
        onClose={() => setIsAuthModalOpen(false)} 
      />
      
      {/* Performance Dashboard (Development Only) */}
      <PerformanceDashboard />
      
      {/* Full Screen Player */}
      <FullScreenPlayer
        isOpen={fullScreenPlayerOpen}
        onClose={handleCloseAllPlayers}
      />
      
      {/* Mini Player */}
      <MiniPlayer
        isVisible={miniPlayerOpen}
        onClose={handleCloseAllPlayers}
        onMaximize={handleMiniPlayerMaximize}
        initialPosition={{ x: 20, y: window.innerHeight - 180 }}
        initialSize={{ width: 350, height: 160 }}
      />
      
      {/* Desktop Sidebar */}
      <Sidebar
        isCollapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      
      {/* Mobile Sidebar */}
      <Sidebar
        isMobile={true}
        isCollapsed={false}
        isOpen={mobileSidebarOpen}
        onClose={() => setMobileSidebarOpen(false)}
        onToggle={() => setMobileSidebarOpen(false)}
      />

      {/* Main content area */}
      <div className={`flex-1 flex flex-col min-w-0 overflow-hidden transition-all duration-300 ${
        sidebarCollapsed ? 'md:ml-16' : 'md:ml-64'
      }`}>
        {/* Enhanced Header */}
        <Header 
          onToggleSidebar={handleToggleSidebar}
          onOpenAuthModal={() => setIsAuthModalOpen(true)}
          sidebarCollapsed={sidebarCollapsed}
        />
        
        {/* Main content with proper spacing for player */}
        <main 
          className="flex-1 overflow-y-auto pb-24 md:pb-28"
          role="main"
          aria-label={`${getPageName(location.pathname)} content`}
        >
          <div className="animate-fade-in">
            {children}
          </div>
        </main>
      </div>

      {/* Enhanced Player controls - fixed to bottom */}
      {playerMode === 'normal' && (
        <div className={`fixed bottom-0 right-0 z-40 transition-all duration-300 ${
          sidebarCollapsed ? 'left-16' : 'left-0 md:left-64'
        }`}>
          <div className="relative">
            {/* Player mode toggle buttons */}
            {player.currentSong && (
              <div className="absolute -top-12 right-4 flex space-x-2">
                <button
                  onClick={handleOpenMiniPlayer}
                  className="p-2 bg-surface-raised border border-surface-border rounded-lg text-text-secondary hover:text-pearl-white hover:bg-naia-pink/20 transition-all duration-200 elevation-2 hover:elevation-3"
                  aria-label="Open mini player"
                  title="Open mini player"
                >
                  <PictureInPictureIcon className="w-5 h-5" />
                </button>
                <button
                  onClick={handleOpenFullScreenPlayer}
                  className="p-2 bg-surface-raised border border-surface-border rounded-lg text-text-secondary hover:text-pearl-white hover:bg-naia-pink/20 transition-all duration-200 elevation-2 hover:elevation-3"
                  aria-label="Open full screen player"
                  title="Open full screen player"
                >
                  <FullscreenIcon className="w-5 h-5" />
                </button>
              </div>
            )}
            
            <PlayerControls />
          </div>
        </div>
      )}
    </div>
  );
} 